import { ArrowRight, RefreshCw, ShieldCheck, UserRound, UsersRound } from "lucide-react";
import type { OrgUserRole } from "@/types/firestore";
import { portalRoleLabels } from "@/lib/portalNavigation";
import { WorkspaceCard, type WorkspaceSummary } from "@/components/portal/WorkspaceCard";

interface MetricProps {
  label: string;
  value: string | number;
  note?: string;
}

function Metric({ label, value, note }: MetricProps) {
  return (
    <div className="rounded-xl border border-[#dfe5df] bg-[#fffefb] p-4 shadow-[0_7px_22px_rgba(23,79,57,0.04)]">
      <p className="font-body text-[10px] font-extrabold uppercase tracking-[0.13em] text-[#647068]">{label}</p>
      <p className="mt-2 font-heading text-3xl font-bold text-[#17221c]">{value}</p>
      {note ? <p className="mt-1 font-body text-[10px] leading-4 text-[#7a847e]">{note}</p> : null}
    </div>
  );
}

export interface PortalDashboardProps {
  organizationName: string;
  adminName: string;
  role: OrgUserRole | null;
  workspaces: WorkspaceSummary[];
  workspacesLoading: boolean;
  workspacesError?: string | null;
  workspacesScopeNote?: string | null;
  totalUsers: number | null;
  activeUsers: number | null;
  countsLoading: boolean;
  countsError: string | null;
  perWorkspaceUserCounts: Map<string, number>;
  userSliceLoading: boolean;
  userSliceError: string | null;
  userScopeNote: string | null;
  onRefreshCounts: () => void;
  onOpenWorkspace: (workspaceId: string) => void;
  onViewWorkspaces: () => void;
  onViewUsers: () => void;
  onViewTeam: () => void;
}

export function PortalDashboard({
  organizationName,
  adminName,
  role,
  workspaces,
  workspacesLoading,
  workspacesError,
  workspacesScopeNote,
  totalUsers,
  activeUsers,
  countsLoading,
  countsError,
  perWorkspaceUserCounts,
  userSliceLoading,
  userSliceError,
  userScopeNote,
  onRefreshCounts,
  onOpenWorkspace,
  onViewWorkspaces,
  onViewUsers,
  onViewTeam,
}: PortalDashboardProps) {
  const countValue = (value: number | null) =>
    countsLoading ? "Loading" : countsError || value === null ? "Unavailable" : value;
  const workspaceValue = workspacesLoading ? "Loading" : workspacesError ? "Unavailable" : workspaces.length;
  const userCountState = userSliceLoading
    ? "loading"
    : userSliceError
      ? "error"
      : "ready";

  return (
    <div className="mx-auto max-w-7xl">
      <header className="mb-7 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <p className="font-body text-[10px] font-extrabold uppercase tracking-[0.17em] text-[#277454]">
            Client Portal / Dashboard
          </p>
          <h1 className="mt-2 font-heading text-4xl font-bold text-[#17221c] sm:text-5xl">
            Welcome, {adminName || "Administrator"}
          </h1>
          <p className="mt-2 font-body text-sm leading-6 text-[#5f6963]">
            {organizationName} workspaces and user access at a glance.
          </p>
        </div>
        <div className="self-start rounded-lg border border-[#bad0c0] bg-[#f0f7f2] px-3 py-2 sm:self-auto">
          <p className="font-body text-[9px] font-extrabold uppercase tracking-wide text-[#277454]">Portal role</p>
          <p className="mt-0.5 font-body text-xs font-bold text-[#385344]">
            {role ? portalRoleLabels[role] || "Organization context unavailable" : "Organization context unavailable"}
          </p>
        </div>
      </header>

      <section aria-label="Organization metrics" className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <Metric label="Workspaces" value={workspaceValue} note={workspacesScopeNote || "Available to your organization"} />
        <Metric label="User records" value={countValue(totalUsers)} note="Across your organization" />
        <Metric label="Active user records" value={countValue(activeUsers)} note="With active status" />
        <Metric
          label="Portal access"
          value={role === "ORG_OWNER" ? "Owner" : role === "ORG_ADMIN" ? "Admin" : "Unavailable"}
          note="Organization administrator account"
        />
      </section>

      {!countsError ? (
        <div className="mt-3 flex justify-end">
          <button
            type="button"
            onClick={onRefreshCounts}
            disabled={countsLoading}
            className="inline-flex items-center gap-2 font-body text-xs font-bold text-[#277454] hover:text-[#174f39] disabled:opacity-50"
          >
            <RefreshCw className={countsLoading ? "h-3.5 w-3.5 animate-spin" : "h-3.5 w-3.5"} />
            Refresh user totals
          </button>
        </div>
      ) : null}

      {countsError ? (
        <div role="alert" className="mt-4 flex flex-col gap-3 rounded-xl border border-[#e4b8af] bg-[#fff3f0] p-4 sm:flex-row sm:items-center sm:justify-between">
          <p className="font-body text-sm text-[#863c32]">{countsError} Aggregate totals are unavailable.</p>
          <button
            type="button"
            onClick={onRefreshCounts}
            disabled={countsLoading}
            className="inline-flex items-center gap-2 self-start font-body text-xs font-bold text-[#863c32] disabled:opacity-50"
          >
            <RefreshCw className={countsLoading ? "h-3.5 w-3.5 animate-spin" : "h-3.5 w-3.5"} />
            Retry counts
          </button>
        </div>
      ) : null}

      <section className="mt-6 rounded-xl border border-[#bad0c0] bg-[#f0f7f2] p-5">
        <div className="flex gap-3">
          <ShieldCheck className="mt-0.5 h-5 w-5 shrink-0 text-[#277454]" />
          <div>
            <h2 className="font-heading text-lg font-bold text-[#243c2f]">People have different kinds of access</h2>
            <p className="mt-1 max-w-4xl font-body text-sm leading-6 text-[#4f6257]">
              Portal Team members administer {organizationName}. The Users view can include Studio users, workshop contacts, and contacts. Workshop registrants use their signed workshop links. These identities remain separate.
            </p>
          </div>
        </div>
      </section>

      <section className="mt-8">
        <div className="mb-4 flex items-end justify-between gap-4">
          <div>
            <p className="font-body text-[10px] font-extrabold uppercase tracking-[0.15em] text-[#277454]">Your organization</p>
            <h2 className="mt-1 font-heading text-2xl font-bold text-[#17221c]">Open a workspace</h2>
          </div>
          <button type="button" onClick={onViewWorkspaces} className="inline-flex items-center gap-1 font-body text-xs font-bold text-[#277454] hover:text-[#174f39]">
            View all <ArrowRight className="h-3.5 w-3.5" />
          </button>
        </div>

        {userScopeNote ? (
          <p className="mb-3 rounded-lg border border-[#e3c47b] bg-[#fff7df] px-3 py-2 font-body text-xs leading-5 text-[#6c5016]">
            Workspace user counts: {userScopeNote}
          </p>
        ) : null}
        {workspacesScopeNote ? (
          <p className="mb-3 rounded-lg border border-[#bad0c0] bg-[#f0f7f2] px-3 py-2 font-body text-xs leading-5 text-[#385344]">
            {workspacesScopeNote}
          </p>
        ) : null}
        {workspacesError ? (
          <div role="alert" className="rounded-xl border border-[#e4b8af] bg-[#fff3f0] p-5 font-body text-sm text-[#863c32]">{workspacesError}</div>
        ) : workspacesLoading ? (
          <div role="status" className="rounded-xl border border-[#dfe5df] bg-[#fffefb] p-8 text-center font-body text-sm text-[#647068]">Loading workspaces...</div>
        ) : workspaces.length === 0 ? (
          <div className="rounded-xl border border-dashed border-[#bfcac2] bg-[#fffefb] p-8 text-center">
            <BuildingEmptyState />
          </div>
        ) : (
          <div className="grid gap-4 xl:grid-cols-2">
            {workspaces.slice(0, 2).map((workspace) => (
              <WorkspaceCard
                key={workspace.id}
                workspace={workspace}
                userCount={perWorkspaceUserCounts.get(workspace.id) ?? 0}
                userCountState={userCountState}
                onOpen={onOpenWorkspace}
              />
            ))}
          </div>
        )}
      </section>

      <section className="mt-6 grid gap-4 md:grid-cols-2">
        <QuickLink
          icon={UserRound}
          title="Users"
          copy="Review organization user records across workspaces, then open the available access and communication controls."
          action="View users"
          onClick={onViewUsers}
        />
        <QuickLink
          icon={UsersRound}
          title="Portal Team"
          copy={
            role === "ORG_OWNER"
              ? "Invite and manage organization administrators."
              : role === "ORG_ADMIN"
                ? "View the administrators who manage this Client Portal."
                : "Organization context is required to view the portal team."
          }
          action="View team"
          onClick={onViewTeam}
        />
      </section>
    </div>
  );
}

function BuildingEmptyState() {
  return (
    <>
      <p className="font-heading text-lg font-bold text-[#17221c]">No workspaces are available</p>
      <p className="mx-auto mt-1 max-w-md font-body text-sm leading-6 text-[#647068]">
        A BiG platform administrator must create and configure the Studio before it appears here.
      </p>
    </>
  );
}

interface QuickLinkProps {
  icon: typeof UserRound;
  title: string;
  copy: string;
  action: string;
  onClick: () => void;
}

function QuickLink({ icon: Icon, title, copy, action, onClick }: QuickLinkProps) {
  return (
    <article className="rounded-xl border border-[#dfe5df] bg-[#fffefb] p-5">
      <Icon className="h-5 w-5 text-[#277454]" />
      <h2 className="mt-5 font-heading text-lg font-bold text-[#17221c]">{title}</h2>
      <p className="mt-1 min-h-12 font-body text-sm leading-6 text-[#5f6963]">{copy}</p>
      <button type="button" onClick={onClick} className="mt-4 inline-flex items-center gap-1 font-body text-xs font-bold text-[#277454] hover:text-[#174f39]">
        {action} <ArrowRight className="h-3.5 w-3.5" />
      </button>
    </article>
  );
}
